import type { ReactNode } from 'react';

// Original 24-unit portal symbols. No raster assets, shared IDs or paint servers.
// The surrounding icon owns size, stroke, accessible naming and color.
const handset = 'M7.8 3.6 9.4 7a1.5 1.5 0 0 1-.3 1.7L7.6 10a14.4 14.4 0 0 0 6.4 6.4l1.3-1.5a1.5 1.5 0 0 1 1.7-.3l3.4 1.6a1.5 1.5 0 0 1 .8 1.7l-.5 2a1.8 1.8 0 0 1-1.9 1.3A18.5 18.5 0 0 1 2.8 5.2 1.8 1.8 0 0 1 4.1 3.3l2-.5a1.5 1.5 0 0 1 1.7.8Z';
const gear = Array.from({ length: 8 }, (_, tooth) => [-14, -9, 9, 14].map((offset, corner) => {
  const angle = (tooth * 45 + offset) * Math.PI / 180;
  const radius = corner === 0 || corner === 3 ? 7.4 : 9.4;
  return `${(12 + Math.cos(angle) * radius).toFixed(3)} ${(12 + Math.sin(angle) * radius).toFixed(3)}`;
})).flat().join(' L');

const symbols: Readonly<Record<string, ReactNode>> = {
  LayoutDashboard: <>
    <rect className="symbol-body" x="3" y="3" width="7.5" height="10.5" rx="2.2" />
    <rect className="symbol-solid" x="13.5" y="3" width="7.5" height="6.5" rx="2.2" />
    <rect className="symbol-solid" x="3" y="16.5" width="7.5" height="4.5" rx="1.8" />
    <rect className="symbol-body" x="13.5" y="12.5" width="7.5" height="8.5" rx="2.2" />
  </>,
  Ticket: <>
    <path className="symbol-body" d="M5 5h14a2 2 0 0 1 2 2v2a3 3 0 0 0 0 6v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a3 3 0 0 0 0-6V7a2 2 0 0 1 2-2Z" />
    <path className="symbol-detail" d="M8.5 8v1m0 2.5v1m0 2.5v1" />
    <path d="M12 9.5h5m-5 5h3.5" />
  </>,
  Headphones: <>
    <path d="M4.3 13V10a7.7 7.7 0 0 1 15.4 0v3" />
    <rect className="symbol-body" x="2.5" y="11" width="5" height="8" rx="2.3" />
    <rect className="symbol-body" x="16.5" y="11" width="5" height="8" rx="2.3" />
    <path className="symbol-detail" d="M19 19c0 2-2.1 2-5 2" /><rect className="symbol-solid" x="10.5" y="19.5" width="4" height="3" rx="1.5" />
  </>,
  Phone: <path className="symbol-body" d={handset} />,
  PhoneCall: <><path className="symbol-body" d={handset} /><path d="M14 3.5a6.5 6.5 0 0 1 6.5 6.5M14 7a3 3 0 0 1 3 3" /></>,
  Activity: <>
    <rect className="symbol-body" x="2.5" y="4" width="19" height="16" rx="4" />
    <path d="M5.5 12h3l2-4.5 3 9 2-4.5h3" />
  </>,
  Building2: <>
    <rect className="symbol-body" x="7" y="3" width="10" height="18" rx="2.3" />
    <path className="symbol-detail" d="M7 10H4.5A1.5 1.5 0 0 0 3 11.5V21h18V9.5A1.5 1.5 0 0 0 19.5 8H17" />
    <path d="M10.5 7h3m-3 4h3m-3 4h3M12 19v2" />
  </>,
  Users: <>
    <circle className="symbol-body" cx="15.7" cy="7.2" r="3.2" />
    <path className="symbol-body" d="M13 13.5c4.3-1.4 8.5 1.2 8.5 4.9v1.1H16" />
    <circle className="symbol-solid" cx="8.4" cy="8" r="3.6" />
    <path className="symbol-body" d="M2.5 19.2c0-3.5 2.2-5.5 5.9-5.5s5.9 2 5.9 5.5V21H2.5Z" />
  </>,
  BookOpen: <>
    <path className="symbol-body" d="M12 6C9.5 3.7 6.3 3.5 3 4.4v14.2c3.3-.9 6.5-.7 9 1.6 2.5-2.3 5.7-2.5 9-1.6V4.4c-3.3-.9-6.5-.7-9 1.6Z" />
    <path d="M12 6v14M6 8h3m-3 4h3m6-4h3m-3 4h3" />
  </>,
  CalendarClock: <>
    <path className="symbol-body" d="M10.5 21H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v4" />
    <path d="M7 2.5v4m8-4v4M3 9h17" />
    <circle className="symbol-body" cx="16.5" cy="16.5" r="5" /><path d="M16.5 13.8v3l2 1.1" />
    <rect className="symbol-solid" x="6" y="12" width="2.5" height="2.5" rx=".7" />
  </>,
  Mail: <>
    <rect className="symbol-body" x="2.5" y="4.5" width="19" height="15" rx="3.4" />
    <path d="m4 6 6.6 5.6a2.2 2.2 0 0 0 2.8 0L20 6" />
    <path className="symbol-detail" d="m3.5 18 5-5m11.9 5-5-5" />
  </>,
  CircleDollarSign: <>
    <circle className="symbol-body" cx="12" cy="12" r="9" />
    <path d="M15.2 8.2h-4.6a2 2 0 0 0 0 4h2.8a2 2 0 0 1 0 4H8.8M12 6v12" />
  </>,
  Bell: <>
    <path className="symbol-body" d="M5.5 10a6.5 6.5 0 0 1 13 0c0 3.5.8 4.4 1.5 5.6.5.9.1 1.9-1.1 1.9H5.1c-1.2 0-1.6-1-1.1-1.9.7-1.2 1.5-2.1 1.5-5.6Z" />
    <path d="M9.5 20a2.8 2.8 0 0 0 5 0M12 2v1.5" />
  </>,
  Settings: <><path className="symbol-body" d={`M${gear}Z`} /><circle cx="12" cy="12" r="3.1" /></>,
  Server: <>
    <rect className="symbol-body" x="3" y="3.5" width="18" height="7" rx="2.5" />
    <rect className="symbol-body" x="3" y="13.5" width="18" height="7" rx="2.5" />
    <path d="M7 7h5m-5 10h5" />
    <circle className="symbol-solid" cx="17.3" cy="7" r="1" /><circle className="symbol-solid" cx="17.3" cy="17" r="1" />
  </>,
  ShieldCheck: <>
    <path className="symbol-body" d="M12 2.7c2.6 1.7 5.1 2.5 8 3.1v6.1c0 4.5-3.3 7.4-8 9.6-4.7-2.2-8-5.1-8-9.6V5.8c2.9-.6 5.4-1.4 8-3.1Z" />
    <path d="m8.2 11.8 2.5 2.5 5.1-5.1" />
  </>,
  Sparkles: <>
    <path className="symbol-body" d="m10 4 1.9 5.7L17.5 12l-5.6 2.3L10 20l-2-5.7L2.5 12 8 9.7Z" />
    <path className="symbol-solid" d="m19 1.5 1.1 3.4L23.5 6l-3.4 1.1L19 10.5l-1.1-3.4L14.5 6l3.4-1.1ZM19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8Z" />
  </>,
  Network: <>
    <path className="symbol-detail" d="M12 9v5m-6 3v-3h12v3" />
    <rect className="symbol-body" x="8" y="2.5" width="8" height="6.5" rx="1.9" />
    <rect className="symbol-body" x="2.5" y="16" width="7" height="5.5" rx="1.7" /><rect className="symbol-body" x="14.5" y="16" width="7" height="5.5" rx="1.7" />
  </>,
  BarChart3: <>
    <path className="symbol-detail" d="M3 3v16a2 2 0 0 0 2 2h16" />
    <rect className="symbol-body" x="6.5" y="11" width="3" height="6.5" rx="1" />
    <rect className="symbol-solid" x="12" y="5" width="3" height="12.5" rx="1" />
    <rect className="symbol-body" x="17.5" y="8" width="3" height="9.5" rx="1" />
  </>,
  Inbox: <>
    <path className="symbol-body" d="m3 12 3-7h12l3 7v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    <path d="M3 12h5l1.5 3h5l1.5-3h5" /><path className="symbol-detail" d="M9 8h6" />
  </>,
  MessageCircle: <>
    <path className="symbol-body" d="M21 11.5c0 4.7-4 8.5-9 8.5a10 10 0 0 1-4.2-.9L3 21l1.4-4.7A8 8 0 0 1 3 11.5C3 6.8 7 3 12 3s9 3.8 9 8.5Z" />
    <path d="M7.7 9.5h8.6m-8.6 4h5.4" />
  </>,
  CircleDot: <><circle className="symbol-body" cx="12" cy="12" r="9" /><circle className="symbol-solid" cx="12" cy="12" r="3.2" /></>,
  CheckCircle2: <><circle className="symbol-body" cx="12" cy="12" r="9" /><path d="m7.5 12 3 3 6-6" /></>,
  AlertTriangle: <>
    <path className="symbol-body" d="M10.2 4a2.1 2.1 0 0 1 3.6 0l7 12.3A2.2 2.2 0 0 1 19 19.5H5a2.2 2.2 0 0 1-1.8-3.2Z" />
    <path d="M12 8v4.8" /><circle className="symbol-solid" cx="12" cy="16.2" r="1" />
  </>,
  Clock3: <><circle className="symbol-body" cx="12" cy="12" r="9" /><path d="M12 6.5V12l3.8 2.2" /></>,
  Timer: <>
    <circle className="symbol-body" cx="12" cy="13.5" r="7.7" /><path d="M12 9v4.5l2.8 1.6M9.5 2.5h5M12 3v2.8m5.8 1.8 1.7-1.7" />
  </>,
  Zap: <path className="symbol-body" d="m13.6 2.5-10 11H11l-.7 8 10-11H13Z" />,
  BriefcaseBusiness: <>
    <rect className="symbol-body" x="3" y="6.5" width="18" height="14" rx="3" />
    <path d="M8 6.5V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1.5M3 12a26 26 0 0 0 18 0" />
    <rect className="symbol-solid" x="10.5" y="11.5" width="3" height="4" rx=".9" />
  </>,
  Workflow: <>
    <path className="symbol-detail" d="M8.5 6H17v9m-10-5v8h7" />
    <rect className="symbol-body" x="2.5" y="2.5" width="7" height="7" rx="2" />
    <rect className="symbol-body" x="14" y="14" width="7" height="7" rx="2" />
    <circle className="symbol-solid" cx="17" cy="6" r="2" />
  </>,
  Gauge: <>
    <path className="symbol-body" d="M4 19a9.5 9.5 0 1 1 16 0Z" /><path d="m12 13 4-5M6.5 9.5l1.2.7M12 5.7v1.5m5.5 7H19" />
    <circle className="symbol-solid" cx="12" cy="13" r="1.7" />
  </>,
  Database: <>
    <path className="symbol-body" d="M3.5 6v12c0 2 3.8 3.5 8.5 3.5s8.5-1.5 8.5-3.5V6" />
    <ellipse className="symbol-body" cx="12" cy="6" rx="8.5" ry="3.5" /><path className="symbol-detail" d="M3.5 12c0 2 3.8 3.5 8.5 3.5s8.5-1.5 8.5-3.5" />
  </>,
};

export function portalSymbol(name: string): ReactNode | null {
  const symbol = symbols[name];
  return symbol ? <g className="portal-icon-layer">{symbol}</g> : null;
}
